import type { popstore } from '@ps/types';
import { nanoid } from 'nanoid';

import type { MediaCategory } from '../types/MediaCategory';
import type { UploadProgress } from '../types/UploadProgress';
import {
  type ImageMetadataKey,
  extractImageMetadata,
} from './extractImageMetadata';
import { generateVideoThumbnail } from './generateVideoThumbnail';
import { putToS3 } from './putToS3';
import {
  type RequestPresignedUrlMetadata,
  requestPresignedUrl,
} from './requestPresignedUrl';
import { kMinimumChunkSizeBytes, uploadMultipart } from './uploadMultipart';

export interface UploadMediaItemOptions {
  file: File;
  /** Server-side category — drives metadata persistence + downstream pipelines. */
  category: MediaCategory;
  /**
   * Extra metadata to merge onto the request (caption, tag, post id,
   * etc). Client-extracted image dims / blurhash / brightness are
   * computed automatically; pass `imageMetadata` here to override or
   * suppress.
   */
  metadata?: Omit<
    RequestPresignedUrlMetadata,
    'filename' | 'contentType' | 'size'
  >;
  /**
   * Which client-side metadata keys to extract. Default: all four
   * (width / height / blurhash / brightness). Pass a subset to skip
   * expensive canvas passes when the consumer doesn't need them.
   */
  extractKeys?: ImageMetadataKey[];
  /** Overall progress callback — fires for both single-PUT and multipart flows. */
  onProgress?: (progress: UploadProgress) => void;
  /** Cancels the in-flight upload. */
  signal?: AbortSignal;
}

/**
 * One-shot upload orchestrator. Mirrors PopshopRN's
 * `usePostMediaFileService` / `useCommunityChannelMessageMediaFileService`
 * but collapsed into a single function — the only thing that varied
 * across PopshopRN's per-feature service hooks was the category /
 * tag, which is now a parameter.
 *
 * Pipeline:
 *   1. Extract client-side metadata (dims + blurhash + brightness for
 *      images; first-frame thumbnail metadata for videos).
 *   2. Branch by file size:
 *      - `size >= kMinimumChunkSizeBytes` (20MB) → multipart upload.
 *      - otherwise → request presigned URL + single PUT + markComplete.
 *   3. Return the final {@link popstore.MediaFileInfo}.
 *
 * Standalone async function — no React state. Compose it inside a
 * service hook (`useMediaUpload`) for the cancel-on-unmount story.
 */
export async function uploadMediaItem(
  options: UploadMediaItemOptions,
): Promise<popstore.MediaFileInfo> {
  const { file, category, metadata, onProgress, signal, extractKeys } = options;

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const imageMetadata = await extractMetadataForFile(file, extractKeys);

  // PopshopRN's ImageUploader generates a client-side `uid` via
  // `nanoid(8)` on every successful upload. Mirroring that here so
  // consumers can use it as a React key and/or local-deduplication
  // token before the server-side row id propagates.
  const uid = nanoid(8);

  const mergedMetadata: RequestPresignedUrlMetadata = {
    ...metadata,
    filename: file.name,
    contentType: file.type,
    size: file.size,
    // Caller-passed imageMetadata wins; falls back to what we extracted.
    imageMetadata: metadata?.imageMetadata ?? imageMetadata,
  };

  if (file.size >= kMinimumChunkSizeBytes) {
    const multipart = await uploadMultipart({
      file,
      filename: file.name,
      category,
      metadata: mergedMetadata,
      onProgress,
      signal,
    });
    return buildMediaFileInfo({
      uid,
      id: multipart.objectId,
      name: file.name,
      url: multipart.cdnUrl,
      size: file.size,
      type: file.type,
      metadata: mergedMetadata.imageMetadata,
    });
  }

  const presigned = await requestPresignedUrl({
    category,
    metadata: mergedMetadata,
    signal,
  });

  await putToS3({
    uploadUrl: presigned.uploadUrl,
    file,
    contentType: file.type,
    onProgress,
    signal,
  });

  // `markComplete` is best-effort: the asset is already retrievable
  // via `cdnUrl`; the status flip is just for the gallery row's UI
  // state. Fire-and-forget, log on failure — same policy as
  // PopshopRN's `updateStatus()` callback.
  void presigned.markComplete().catch((error: unknown) => {
    console.warn('uploadMediaItem: markComplete failed', error);
  });

  return buildMediaFileInfo({
    uid,
    id: presigned.objectId,
    name: file.name,
    url: presigned.cdnUrl,
    size: file.size,
    type: file.type,
    metadata: mergedMetadata.imageMetadata,
  });
}

/**
 * Assemble the final {@link popstore.MediaFileInfo}. `sizeInBytes`
 * and `filename` on `metadata` mirror the top-level fields because
 * PopshopRN's MediaFileInfo carries them both ways — top-level for
 * shipping the upload, on `metadata` for consumers that pull
 * everything from one place (custom-button image renderer etc).
 */
function buildMediaFileInfo({
  uid,
  id,
  name,
  url,
  size,
  type,
  metadata,
}: {
  uid: string;
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  metadata: popstore.MediaFileMetadata | undefined;
}): popstore.MediaFileInfo {
  return {
    id,
    uid,
    name,
    url,
    size,
    type,
    source: 'mediagallery',
    metadata: metadata
      ? {
          ...metadata,
          sizeInBytes: metadata.sizeInBytes ?? size,
          filename: metadata.filename ?? name,
        }
      : { sizeInBytes: size, filename: name },
  };
}

async function extractMetadataForFile(
  file: File,
  keys: ImageMetadataKey[] | undefined,
): Promise<popstore.MediaFileMetadata> {
  // Images: dims + blurhash + brightness straight from the source.
  if (file.type.startsWith('image/')) {
    return extractImageMetadata(file, keys ? { keys } : undefined);
  }

  // Videos: generate a thumbnail in-memory, extract metadata from it.
  // Server stores the video URL; the thumbnail is only used as the
  // poster + blurhash placeholder.
  if (file.type.startsWith('video/')) {
    try {
      const thumbnail = await generateVideoThumbnail(file);
      const thumbnailMetadata = await extractImageMetadata(
        thumbnail.blob,
        keys ? { keys } : undefined,
      );
      return {
        // Prefer the source video's natural dims (the thumbnail's are
        // the same modulo encoder quirks, but the video numbers come
        // from the codec).
        width: thumbnail.videoWidth,
        height: thumbnail.videoHeight,
        blurhash: thumbnailMetadata.blurhash,
        brightness: thumbnailMetadata.brightness,
      };
    } catch {
      // Thumbnail extraction is best-effort; the video upload still
      // succeeds with no client-side metadata.
      return {};
    }
  }

  return {};
}
